import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import SectionLabel from "../ui/SectionLabel";
import { BookAuditCTABar } from "../ui/BookAuditButton";

/* ═══════════════════════════════════════
   Card 1 — Study (Observe)
   Monitor with app rows + scanning line + eye
   Loops: scanning highlight cycles through rows
   ═══════════════════════════════════════ */
function StudyAnimation() {
  const containerRef = useRef(null);

  const animateScanning = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rows = container.querySelectorAll(".app-row");
    let current = 0;
    let timer;

    function scanNext() {
      rows.forEach((r) => r.classList.remove("scanning"));
      if (current < rows.length) {
        rows[current].classList.add("scanning");
        current++;
        timer = setTimeout(scanNext, 900);
      } else {
        current = 0;
        timer = setTimeout(scanNext, 1500);
      }
    }
    scanNext();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const cleanup = animateScanning();
    return cleanup;
  }, [animateScanning]);

  return (
    <div className="hiw-card-animation" ref={containerRef}>
      <div className="study-scene">
        {/* Activity dots */}
        <div className="activity-dot ad1" />
        <div className="activity-dot ad2" />
        <div className="activity-dot ad3" />
        <div className="activity-dot ad4" />

        {/* Eye indicator */}
        <div className="eye-indicator">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>

        {/* Monitor */}
        <div className="monitor-frame">
          <div className="monitor-topbar">
            <span className="monitor-dot md-red" />
            <span className="monitor-dot md-yellow" />
            <span className="monitor-dot md-green" />
          </div>
          <div className="monitor-body">
            <div className="app-row">
              <div className="app-icon ai-slack" />
              <div className="app-bar"><div className="app-bar-fill" style={{ width: "72%" }} /></div>
              <span className="app-time">2.4h</span>
            </div>
            <div className="app-row">
              <div className="app-icon ai-sheets" />
              <div className="app-bar"><div className="app-bar-fill" style={{ width: "55%" }} /></div>
              <span className="app-time">1.8h</span>
            </div>
            <div className="app-row">
              <div className="app-icon ai-chrome" />
              <div className="app-bar"><div className="app-bar-fill" style={{ width: "40%" }} /></div>
              <span className="app-time">1.3h</span>
            </div>
            <div className="app-row">
              <div className="app-icon ai-zoom" />
              <div className="app-bar"><div className="app-bar-fill" style={{ width: "25%" }} /></div>
              <span className="app-time">0.8h</span>
            </div>
          </div>
          <div className="scan-line" />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Card 2 — Find (Assess)
   Report card with animated bars + savings
   Loops: bars fill → savings pop → hold → reset → repeat
   ═══════════════════════════════════════ */
function FindAnimation() {
  const containerRef = useRef(null);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const bar1 = container.querySelector(".find-bar1");
    const bar2 = container.querySelector(".find-bar2");
    const bar3 = container.querySelector(".find-bar3");
    const items = container.querySelectorAll(".cost-item");
    const badge = container.querySelector(".savings-badge");

    // Reset everything
    if (bar1) bar1.style.width = "0%";
    if (bar2) bar2.style.width = "0%";
    if (bar3) bar3.style.width = "0%";
    items.forEach((el) => { el.style.opacity = "0"; el.style.transform = "translateX(-10px)"; });
    if (badge) { badge.style.opacity = "0"; badge.style.transform = "translateY(8px) scale(0.96)"; }

    // Animate in sequence
    const timers = [];
    timers.push(setTimeout(() => { if (items[0]) { items[0].style.transition = "opacity 0.5s ease, transform 0.5s ease"; items[0].style.opacity = "1"; items[0].style.transform = "translateX(0)"; } }, 300));
    timers.push(setTimeout(() => { if (items[1]) { items[1].style.transition = "opacity 0.5s ease, transform 0.5s ease"; items[1].style.opacity = "1"; items[1].style.transform = "translateX(0)"; } }, 700));
    timers.push(setTimeout(() => { if (items[2]) { items[2].style.transition = "opacity 0.5s ease, transform 0.5s ease"; items[2].style.opacity = "1"; items[2].style.transform = "translateX(0)"; } }, 1100));
    timers.push(setTimeout(() => { if (bar1) { bar1.style.transition = "width 1.2s cubic-bezier(0.22,1,0.36,1)"; bar1.style.width = "82%"; } }, 800));
    timers.push(setTimeout(() => { if (bar2) { bar2.style.transition = "width 1.2s cubic-bezier(0.22,1,0.36,1)"; bar2.style.width = "58%"; } }, 1200));
    timers.push(setTimeout(() => { if (bar3) { bar3.style.transition = "width 1.2s cubic-bezier(0.22,1,0.36,1)"; bar3.style.width = "35%"; } }, 1600));
    timers.push(setTimeout(() => { if (badge) { badge.style.transition = "opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)"; badge.style.opacity = "1"; badge.style.transform = "translateY(0) scale(1)"; } }, 2000));

    // Loop: hold 3s then restart
    timers.push(setTimeout(() => setCycle((c) => c + 1), 6000));

    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  return (
    <div className="hiw-card-animation" ref={containerRef}>
      <div className="find-scene">
        {/* Magnifier */}
        <div className="magnifier">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>

        <div className="report-card">
          <div className="report-header">
            <div className="report-title-bar">
              <div className="report-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div>
                <div className="report-label">Waste Report</div>
                <div className="report-sublabel">Monthly Analysis</div>
              </div>
            </div>
          </div>
          <div className="report-body">
            <div className="cost-item">
              <span className="cost-label">Data Entry</span>
              <div className="cost-bar-track">
                <div className="cost-bar-fill cbf-red find-bar1" />
              </div>
              <span className="cost-value">$4.2k</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">Report Gen.</span>
              <div className="cost-bar-track">
                <div className="cost-bar-fill cbf-orange find-bar2" />
              </div>
              <span className="cost-value">$2.8k</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">Scheduling</span>
              <div className="cost-bar-track">
                <div className="cost-bar-fill cbf-blue find-bar3" />
              </div>
              <span className="cost-value">$1.5k</span>
            </div>
            <div className="savings-badge">
              <span className="savings-text">Potential Savings</span>
              <span className="savings-amount">$8.5k/mo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Card 3 — Build (Implement)
   Agent card assembling with blocks
   Loops: blocks snap in → checks appear → deploy pops → hold → reset → repeat
   ═══════════════════════════════════════ */
function BuildAnimation() {
  const containerRef = useRef(null);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const blocks = container.querySelectorAll(".build-block");
    const checks = container.querySelectorAll(".block-check");
    const badge = container.querySelector(".deploy-badge");

    // Reset
    blocks.forEach((el) => { el.style.opacity = "0"; el.style.transform = "translateX(20px)"; el.style.transition = "none"; });
    checks.forEach((el) => { el.style.opacity = "0"; el.style.transition = "none"; });
    if (badge) { badge.style.opacity = "0"; badge.style.transform = "scale(0.9)"; badge.style.transition = "none"; }

    const timers = [];

    // Blocks snap in
    timers.push(setTimeout(() => { if (blocks[0]) { blocks[0].style.transition = "opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)"; blocks[0].style.opacity = "1"; blocks[0].style.transform = "translateX(0)"; } }, 600));
    timers.push(setTimeout(() => { if (blocks[1]) { blocks[1].style.transition = "opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)"; blocks[1].style.opacity = "1"; blocks[1].style.transform = "translateX(0)"; } }, 1100));
    timers.push(setTimeout(() => { if (blocks[2]) { blocks[2].style.transition = "opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)"; blocks[2].style.opacity = "1"; blocks[2].style.transform = "translateX(0)"; } }, 1600));

    // Checks pop
    timers.push(setTimeout(() => { if (checks[0]) { checks[0].style.transition = "opacity 0.3s ease"; checks[0].style.opacity = "1"; } }, 1300));
    timers.push(setTimeout(() => { if (checks[1]) { checks[1].style.transition = "opacity 0.3s ease"; checks[1].style.opacity = "1"; } }, 1800));
    timers.push(setTimeout(() => { if (checks[2]) { checks[2].style.transition = "opacity 0.3s ease"; checks[2].style.opacity = "1"; } }, 2300));

    // Deploy badge
    timers.push(setTimeout(() => { if (badge) { badge.style.transition = "opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)"; badge.style.opacity = "1"; badge.style.transform = "scale(1)"; } }, 2800));

    // Loop: hold 3s then restart
    timers.push(setTimeout(() => setCycle((c) => c + 1), 7000));

    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  return (
    <div className="hiw-card-animation" ref={containerRef}>
      <div className="build-scene">
        {/* Connection lines */}
        <div className="connection-line cl1" />
        <div className="connection-line cl2" />

        {/* Gear */}
        <div className="gear-float">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </div>

        {/* Agent Card */}
        <div className="agent-card">
          <div className="agent-avatar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
              <path d="M18 14a6 6 0 0 0-12 0v4h12v-4z" />
              <line x1="9" y1="18" x2="9" y2="22" />
              <line x1="15" y1="18" x2="15" y2="22" />
            </svg>
          </div>
          <div className="agent-name">AI Agent — Ops</div>
          <div className="agent-type">Custom built for your stack</div>

          <div className="build-blocks">
            <div className="build-block">
              <div className="block-dot bd-green" />
              <span className="block-label">Data Pipeline</span>
              <div className="block-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
            </div>
            <div className="build-block">
              <div className="block-dot bd-blue" />
              <span className="block-label">Auto-Reporting</span>
              <div className="block-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
            </div>
            <div className="build-block">
              <div className="block-dot bd-purple" />
              <span className="block-label">Smart Scheduling</span>
              <div className="block-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
            </div>
          </div>

          <div className="deploy-badge">
            <div className="deploy-dot" />
            <span className="deploy-text">Deployed to your env</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Step cards data ── */
const steps = [
  {
    step: 1,
    title: "Observe",
    desc: "Understand how your business operates day-to-day — across teams, tools, and processes — without disrupting your work.",
    Animation: StudyAnimation,
  },
  {
    step: 2,
    title: "Assess",
    desc: "Identify where time and money are being lost, and clearly see which processes can be improved or automated.",
    Animation: FindAnimation,
  },
  {
    step: 3,
    title: "Implement",
    desc: "We build and deploy tailored AI solutions within your systems — improving efficiency while keeping full control in your hands.",
    Animation: BuildAnimation,
  },
];

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.15 });

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="hiw-section"
    >
      <div className="hiw-container">
        <SectionLabel text="HOW IT WORKS" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="hiw-heading">
            We watch. We find.{" "}
            <span className="hiw-heading-accent">We build.</span>
          </h2>
          <p className="hiw-sub">
            No guesswork. No assumptions. Here's exactly what happens
            when you work with us.
          </p>
        </motion.div>

        {/* Video */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="hiw-video">
            <iframe
              src="https://drive.google.com/file/d/18VaJNFyrBj9vdQ371Auqw43JIuTRv0ru/preview"
              title="How it works"
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="hiw-video-iframe"
            />
          </div>
        </motion.div>

        {/* Three step cards */}
        <div className="hiw-steps">
          {steps.map((step, i) => {
            const AnimationComponent = step.Animation;
            return (
              <motion.div
                key={step.title}
                className="hiw-step"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
              >
                <AnimationComponent />
                <div className="hiw-step-content">
                  <div className="hiw-step-num">{step.step}</div>
                  <h3 className="hiw-step-title">{step.title}</h3>
                  <p className="hiw-step-desc">{step.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <BookAuditCTABar
            title="Want us to do this for your organisation?"
            subtitle="Step 1 is a free 30-minute audit. We map 3-5 areas where automation pays for itself."
            buttonLabel="Start with a free audit"
          />
        </motion.div>
      </div>

      <style>{`
        .hiw-section {
          position: relative;
          overflow: hidden;
          padding: var(--section-padding-y) 0;
          background: var(--bg-base);
        }

        .hiw-container {
          max-width: var(--container-max);
          margin: 0 auto;
          padding: 0 var(--container-padding);
        }

        /* ── Heading ── */
        .hiw-heading {
          font-weight: 900;
          font-size: clamp(36px, 5vw, 62px);
          color: var(--text-primary);
          line-height: 1.1;
          letter-spacing: -0.035em;
          margin: 8px 0 18px;
        }

        .hiw-heading-accent {
          background: linear-gradient(135deg, var(--accent) 0%, #5de0db 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-style: italic;
        }

        .hiw-sub {
          font-size: 16px;
          color: var(--text-muted);
          margin: 0 0 40px;
          line-height: 1.6;
          max-width: 480px;
        }

        /* ── Video ── */
        .hiw-video {
          width: 100%;
          aspect-ratio: 16 / 9;
          border-radius: 20px;
          background: #000;
          border: 1px solid rgba(0,0,0,0.06);
          overflow: hidden;
          margin-bottom: 28px;
        }

        .resource-optimizer-page:not(.light) .hiw-video {
          border-color: var(--border-default);
        }

        .hiw-video-iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        /* ══════════════════════════════
           Step Cards Grid
           ══════════════════════════════ */
        .hiw-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .hiw-step {
          background: #fff;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #E8EBF0;
          box-shadow: 0 4px 24px rgba(15,23,36,0.06), 0 1px 4px rgba(15,23,36,0.04);
          transition: transform 0.4s ease, box-shadow 0.4s ease;
          cursor: default;
        }

        .hiw-step:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(15,23,36,0.1), 0 2px 8px rgba(15,23,36,0.06);
        }

        .resource-optimizer-page:not(.light) .hiw-step {
          background: var(--bg-elevated);
          border-color: var(--border-default);
        }

        .resource-optimizer-page:not(.light) .hiw-step:hover {
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
        }

        /* ── Card Animation Area ── */
        .hiw-card-animation {
          height: 280px;
          position: relative;
          background: linear-gradient(180deg, #F8FAFD 0%, #FFFFFF 100%);
          border-bottom: 1px solid #E8EBF0;
          overflow: hidden;
        }

        .hiw-card-animation::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(40,180,174,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(40,180,174,0.03) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }

        .resource-optimizer-page:not(.light) .hiw-card-animation {
          background: linear-gradient(180deg, rgba(40,180,174,0.04) 0%, var(--bg-elevated) 100%);
          border-bottom-color: var(--border-default);
        }

        .resource-optimizer-page:not(.light) .hiw-card-animation::before {
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
        }

        /* ── Card Content ── */
        .hiw-step-content {
          padding: 28px 28px 32px;
        }

        .hiw-step-num {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: #28B4AE;
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 14px;
        }

        .hiw-step-title {
          font-weight: 700;
          font-size: 22px;
          color: var(--text-primary);
          margin: 0 0 10px;
          letter-spacing: -0.01em;
        }

        .hiw-step-desc {
          font-size: 15px;
          color: var(--text-muted);
          line-height: 1.65;
          margin: 0;
        }

        /* ══════════════════════════════
           STUDY Animation
           ══════════════════════════════ */
        .study-scene, .find-scene, .build-scene {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .monitor-frame {
          width: 260px;
          height: 180px;
          background: #fff;
          border-radius: 14px;
          border: 1.5px solid #E2E6ED;
          box-shadow: 0 8px 30px rgba(15,23,36,0.08);
          position: relative;
          overflow: hidden;
        }

        .monitor-topbar {
          height: 28px;
          background: #F3F5F8;
          border-bottom: 1px solid #E8EBF0;
          display: flex;
          align-items: center;
          padding: 0 10px;
          gap: 5px;
        }

        .monitor-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
        }

        .md-red { background: #FF6058; }
        .md-yellow { background: #FFBD2E; }
        .md-green { background: #28CA42; }

        .monitor-body {
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 7px;
          height: calc(100% - 28px);
          position: relative;
        }

        .app-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 8px;
          background: #F8F9FB;
          border: 1px solid transparent;
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
        }

        .app-row.scanning {
          border-color: rgba(40,180,174,0.3);
          background: rgba(40,180,174,0.08);
        }

        .app-icon {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .ai-slack { background: linear-gradient(135deg, #E01E5A, #ECB22E, #2EB67D, #36C5F0); }
        .ai-sheets { background: linear-gradient(135deg, #0F9D58, #34A853); }
        .ai-chrome { background: linear-gradient(135deg, #4285F4, #EA4335, #FBBC05, #34A853); }
        .ai-zoom { background: linear-gradient(135deg, #2D8CFF, #0B5CFF); }

        .app-bar {
          height: 5px;
          border-radius: 3px;
          background: #E2E6ED;
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .app-bar-fill {
          height: 100%;
          border-radius: 3px;
          background: #C8CED8;
          transition: width 0.6s ease;
        }

        .app-time {
          font-size: 10px;
          color: #5A6478;
          font-weight: 500;
          min-width: 28px;
          text-align: right;
        }

        .scan-line {
          position: absolute;
          top: 28px;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #28B4AE, transparent);
          opacity: 0;
          animation: hiwScanDown 3s ease-in-out infinite;
        }

        @keyframes hiwScanDown {
          0% { top: 28px; opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { top: 180px; opacity: 0; }
        }

        .eye-indicator {
          position: absolute;
          top: 14px;
          right: 16px;
          width: 36px;
          height: 36px;
          background: rgba(40,180,174,0.12);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: hiwEyePulse 2s ease-in-out infinite;
        }

        .eye-indicator svg {
          width: 18px;
          height: 18px;
          color: #28B4AE;
        }

        @keyframes hiwEyePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(40,180,174,0.12); }
          50% { transform: scale(1.08); box-shadow: 0 0 0 8px transparent; }
        }

        .activity-dot {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          opacity: 0;
        }

        .ad1 { top: 24px; left: 30px; background: #22C55E; animation: hiwDotAppear 3s 0s infinite; }
        .ad2 { top: 50px; right: 20px; background: #F59E0B; animation: hiwDotAppear 3s 0.8s infinite; }
        .ad3 { bottom: 40px; left: 20px; background: #28B4AE; animation: hiwDotAppear 3s 1.6s infinite; }
        .ad4 { bottom: 24px; right: 35px; background: #8B5CF6; animation: hiwDotAppear 3s 2.4s infinite; }

        @keyframes hiwDotAppear {
          0%, 100% { opacity: 0; transform: scale(0); }
          20% { opacity: 1; transform: scale(1); }
          80% { opacity: 1; transform: scale(1); }
          95% { opacity: 0; transform: scale(0); }
        }

        /* ══════════════════════════════
           FIND Animation
           ══════════════════════════════ */
        .report-card {
          width: 260px;
          background: #fff;
          border-radius: 14px;
          border: 1.5px solid #E2E6ED;
          box-shadow: 0 8px 30px rgba(15,23,36,0.08);
          overflow: hidden;
        }

        .report-header {
          padding: 14px 16px 10px;
          border-bottom: 1px solid #F0F2F5;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .report-title-bar {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .report-icon {
          width: 28px;
          height: 28px;
          background: #FEE2E2;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .report-icon svg { width: 14px; height: 14px; color: #EF4444; }

        .report-label {
          font-size: 12px;
          font-weight: 600;
          color: #0F1724;
        }

        .report-sublabel {
          font-size: 10px;
          color: #5A6478;
          font-weight: 400;
        }

        .report-body { padding: 14px 16px 16px; }

        .cost-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          opacity: 0;
          transform: translateX(-10px);
        }

        .cost-label {
          font-size: 11px;
          color: #5A6478;
          min-width: 68px;
          font-weight: 500;
        }

        .cost-bar-track {
          flex: 1;
          height: 8px;
          background: #F0F2F5;
          border-radius: 4px;
          overflow: hidden;
        }

        .cost-bar-fill {
          height: 100%;
          border-radius: 4px;
          width: 0%;
          transition: width 1.2s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .cbf-red { background: linear-gradient(90deg, #FCA5A5, #EF4444); }
        .cbf-orange { background: linear-gradient(90deg, #FCD34D, #F59E0B); }
        .cbf-blue { background: linear-gradient(90deg, #5EEAD4, #14B8A6); }

        .cost-value {
          font-size: 11px;
          font-weight: 700;
          color: #0F1724;
          min-width: 38px;
          text-align: right;
        }

        .savings-badge {
          margin-top: 14px;
          padding: 10px 14px;
          background: #DCFCE7;
          border-radius: 10px;
          border: 1px solid rgba(34,197,94,0.2);
          display: flex;
          align-items: center;
          justify-content: space-between;
          opacity: 0;
          transform: translateY(8px) scale(0.96);
        }

        .savings-text { font-size: 11px; color: #166534; font-weight: 600; }
        .savings-amount { font-size: 16px; font-weight: 700; color: #166534; }

        .magnifier {
          position: absolute;
          top: 18px;
          right: 18px;
          width: 38px;
          height: 38px;
          background: #FEF3C7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: hiwMagnifierBob 2.5s ease-in-out infinite;
        }

        .magnifier svg { width: 18px; height: 18px; color: #F59E0B; }

        @keyframes hiwMagnifierBob {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-4px) rotate(-8deg); }
        }

        /* ══════════════════════════════
           BUILD Animation
           ══════════════════════════════ */
        .agent-card {
          width: 220px;
          background: #fff;
          border-radius: 14px;
          border: 1.5px solid #E2E6ED;
          box-shadow: 0 8px 30px rgba(15,23,36,0.08);
          padding: 18px;
          position: relative;
        }

        .agent-avatar {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #28B4AE, #8B5CF6);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          animation: hiwAgentPulse 2s ease-in-out infinite;
        }

        .agent-avatar svg { width: 22px; height: 22px; color: #fff; }

        @keyframes hiwAgentPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(40,180,174,0.2); }
          50% { box-shadow: 0 0 0 10px rgba(40,180,174,0); }
        }

        .agent-name { font-size: 13px; font-weight: 700; color: #0F1724; margin-bottom: 4px; }
        .agent-type { font-size: 10px; color: #5A6478; font-weight: 500; margin-bottom: 14px; }

        .build-blocks { display: flex; flex-direction: column; gap: 6px; }

        .build-block {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 10px;
          background: #F8F9FB;
          border-radius: 8px;
          border: 1px solid #EEF0F4;
          opacity: 0;
          transform: translateX(20px);
        }

        .block-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .bd-green { background: #22C55E; }
        .bd-blue { background: #28B4AE; }
        .bd-purple { background: #8B5CF6; }

        .block-label { font-size: 10px; color: #0F1724; font-weight: 500; }

        .block-check {
          margin-left: auto;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #DCFCE7;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
        }

        .block-check svg { width: 9px; height: 9px; color: #22C55E; }

        .deploy-badge {
          margin-top: 12px;
          padding: 8px 12px;
          border-radius: 8px;
          background: rgba(40,180,174,0.08);
          border: 1px solid rgba(40,180,174,0.15);
          display: flex;
          align-items: center;
          gap: 6px;
          opacity: 0;
          transform: scale(0.9);
        }

        .deploy-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #22C55E;
          animation: hiwLiveBlink 1.5s 3s ease-in-out infinite;
        }

        @keyframes hiwLiveBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .deploy-text { font-size: 11px; font-weight: 600; color: #28B4AE; }

        .gear-float {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          background: #EDE9FE;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: hiwGearSpin 4s linear infinite;
        }

        .gear-float svg { width: 18px; height: 18px; color: #8B5CF6; }

        @keyframes hiwGearSpin {
          to { transform: rotate(360deg); }
        }

        .connection-line {
          position: absolute;
          width: 2px;
          background: linear-gradient(180deg, transparent, #28B4AE, transparent);
          opacity: 0.3;
        }

        .cl1 { height: 40px; top: 10px; left: 40px; animation: hiwLineFlicker 2s 0s infinite; }
        .cl2 { height: 30px; bottom: 20px; right: 30px; animation: hiwLineFlicker 2s 0.7s infinite; }

        @keyframes hiwLineFlicker {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.4; }
        }

        /* ══════════════════════════════
           Responsive
           ══════════════════════════════ */
        @media (max-width: 900px) {
          .hiw-steps {
            grid-template-columns: 1fr;
            max-width: 420px;
            margin: 0 auto;
            gap: 16px;
          }
        }

        @media (max-width: 640px) {
          .hiw-video {
            aspect-ratio: 16 / 10;
            border-radius: 14px;
          }
          .hiw-card-animation {
            height: 240px;
          }
        }
      `}</style>
    </section>
  );
}
